import { reportedAt } from "./TechDeviceWords"
import { TechDeviceState, useTechClock, useTechDeviceState } from "../../core/TechLive"
import Icon from "../base/Icon"

interface IProps {
    techId?: string
    className?: string
    /** Where a row already carries its own icon. */
    noIcon?: boolean
}

/** The battery icon that matches the level, as Font Awesome draws them. */
export const batteryIcon = (level: number): any =>
    level >= 88 ? "battery-full"
        : level >= 63 ? "battery-three-quarters"
            : level >= 38 ? "battery-half"
                : level >= 13 ? "battery-quarter"
                    : "battery-empty"

/** Red when it will not last the day, amber when it may not, green otherwise. */
export const batteryTone = (level: number, charging?: boolean): string =>
    charging ? "success" : level <= 15 ? "danger" : level <= 30 ? "warning" : "success"

/**
 * The phone's battery as it was with the last location — beside the dot on the map, because a
 * technician about to vanish from it is one whose phone is about to die.
 */
export default function TechBatteryLabel(props: IProps) {

    const state = useTechDeviceState(props.techId)
    const level = state?.batteryLevel

    if (level === undefined || level === null) {
        return (
            <span className={`d-inline-flex align-items-center text-gray-500 ${props.className ?? ""}`}>
                {!props.noIcon && <Icon icon="battery-empty" className="me-2 text-gray-400" size={12} />}
                Battery not reported
            </span>
        )
    }

    const tone = batteryTone(level, state?.charging)

    return (
        <span className={`d-inline-flex align-items-center ${props.className ?? ""}`}>
            {!props.noIcon && <Icon icon={batteryIcon(level)} className={`me-2 text-${tone}`} size={12} />}
            <span className="text-gray-600 me-1">Battery:</span>
            <span className={`fw-semibold text-${tone}`}>{level}%</span>
            {state?.charging && <span className="text-success ms-1"><Icon icon="bolt" size={10} /> Charging</span>}
        </span>
    )
}

/** Past this, a battery reading says what it was, not what it is. */
const batteryStaleMs = 30 * 60 * 1000

/**
 * The battery worth showing, or nothing: not reported, older than half an hour, or the
 * technician is off the clock (the phone is not reporting, so the number is from whenever
 * they stopped).
 */
export const currentBattery = (state?: TechDeviceState, onClock?: boolean): { level: number, charging: boolean } | undefined => {
    const level = state?.batteryLevel
    if (level === undefined || level === null || !onClock) return undefined

    const at = reportedAt(state?.batteryAt)
    if (!at || Date.now() - at.getTime() > batteryStaleMs) return undefined

    return { level, charging: state?.charging === true }
}

/**
 * The battery as a compact badge, for the corner of the map's panel: the icon (its charging
 * version while plugged in) and the percentage, or a grey icon alone when there is nothing
 * current to say.
 */
export function TechBatteryBadge(props: { techId?: string, large?: boolean }) {

    // The drawer has room for a little more than the map's panel.
    const iconSize = props.large ? 16 : 13
    const textSize = props.large ? "fs-7" : "fs-8"


    const state = useTechDeviceState(props.techId)
    const clock = useTechClock(props.techId)
    const battery = currentBattery(state, clock?.on)

    if (!battery) {
        return (
            <span className="d-inline-flex align-items-center" title="No current battery reading">
                <Icon icon="battery-empty" type="solid" className="text-gray-400" size={iconSize} />
            </span>
        )
    }

    const tone = batteryTone(battery.level, battery.charging)

    return (
        <span className="d-inline-flex align-items-center" title={battery.charging ? "Charging" : "On battery"}>
            <Icon icon={battery.charging ? "battery-bolt" : batteryIcon(battery.level)} type="solid" className={`text-${tone} me-1`} size={iconSize} />
            <span className={`${textSize} fw-bold text-${tone}`}>{battery.level}%</span>
        </span>
    )
}
